<template>
  <teleport to="body">
    <transition name="dialog">
      <div
        v-if="open"
        class="cd__backdrop"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        @click.self="onCancel"
      >
        <div class="cd__panel" ref="panel">
          <span class="cd__icon" :class="`cd__icon--${tone}`">
            <i :class="iconClass" aria-hidden="true"></i>
          </span>

          <h2 :id="titleId" class="cd__title">{{ title }}</h2>
          <p v-if="message" class="cd__message">{{ message }}</p>

          <!-- Nama benda yang akan dihapus, ditampilkan tegas supaya
               pengguna tahu persis apa yang sedang dikonfirmasi -->
          <p v-if="itemName" class="cd__item">{{ itemName }}</p>

          <p v-if="tone === 'danger'" class="cd__warning">
            <i class="bi bi-exclamation-triangle-fill" aria-hidden="true"></i>
            Tindakan ini tidak dapat dibatalkan.
          </p>

          <div class="cd__actions">
            <button
              ref="cancelBtn"
              type="button"
              class="ui-btn ui-btn--secondary"
              :disabled="busy"
              @click="onCancel"
            >
              {{ cancelText }}
            </button>
            <button
              type="button"
              class="ui-btn"
              :class="tone === 'danger' ? 'ui-btn--danger' : 'ui-btn--primary'"
              :disabled="busy"
              @click="onConfirm"
            >
              <span v-if="busy" class="ui-spinner" aria-hidden="true"></span>
              {{ busy ? 'Memproses...' : confirmText }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
let idCounter = 0;

/**
 * Dialog konfirmasi.
 *
 * Menggantikan window.confirm() yang dipakai di beberapa halaman admin.
 * Masalah window.confirm: tampilannya tidak bisa diatur, membekukan seluruh
 * thread JavaScript, tidak bisa menampilkan keadaan "sedang memproses", dan
 * pada beberapa peramban bisa diblokir sepenuhnya oleh pengguna sehingga
 * penghapusan diam-diam berjalan tanpa konfirmasi.
 *
 * Dipakai lewat komposabel useConfirm() agar pemanggilannya tetap ringkas:
 *   if (await confirm({ title: 'Hapus?', itemName: nama })) { ... }
 */
export default {
  name: 'ConfirmDialog',

  props: {
    open: { type: Boolean, default: false },
    title: { type: String, default: 'Konfirmasi' },
    message: { type: String, default: '' },
    itemName: { type: String, default: '' },
    confirmText: { type: String, default: 'Hapus' },
    cancelText: { type: String, default: 'Batal' },
    tone: { type: String, default: 'danger' },
    busy: { type: Boolean, default: false },
  },

  emits: ['confirm', 'cancel'],

  data() {
    idCounter += 1;
    return { titleId: `cd-title-${idCounter}`, lastFocused: null };
  },

  computed: {
    iconClass() {
      return this.tone === 'danger' ? 'bi bi-trash3' : 'bi bi-question-lg';
    },
  },

  watch: {
    open(nilai) {
      if (nilai) {
        // Simpan elemen yang tadinya fokus agar bisa dikembalikan saat
        // dialog ditutup — penting bagi pengguna keyboard.
        this.lastFocused = document.activeElement;
        document.body.style.overflow = 'hidden';
        this.$nextTick(() => this.$refs.cancelBtn?.focus());
      } else {
        document.body.style.overflow = '';
        this.lastFocused?.focus?.();
      }
    },
  },

  mounted() {
    document.addEventListener('keydown', this.onKeydown);
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
    document.body.style.overflow = '';
  },

  methods: {
    onKeydown(e) {
      if (!this.open) return;
      if (e.key === 'Escape' && !this.busy) this.onCancel();

      // Kurung fokus di dalam dialog selama terbuka.
      if (e.key === 'Tab' && this.$refs.panel) {
        const fokusable = this.$refs.panel.querySelectorAll('button:not([disabled])');
        if (!fokusable.length) return;
        const pertama = fokusable[0];
        const terakhir = fokusable[fokusable.length - 1];
        if (e.shiftKey && document.activeElement === pertama) {
          e.preventDefault();
          terakhir.focus();
        } else if (!e.shiftKey && document.activeElement === terakhir) {
          e.preventDefault();
          pertama.focus();
        }
      }
    },
    onConfirm() { this.$emit('confirm'); },
    onCancel() { if (!this.busy) this.$emit('cancel'); },
  },
};
</script>

<style scoped>
.cd__backdrop {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
  display: grid;
  place-items: center;
  padding: var(--space-5);
  background: var(--overlay-bg);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.cd__panel {
  width: 100%;
  max-width: 420px;
  padding: var(--space-8);
  text-align: center;
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-2xl);
  box-shadow: var(--shadow-xl);
}

.cd__icon {
  display: grid;
  place-items: center;
  width: 60px; height: 60px;
  margin: 0 auto var(--space-5);
  font-size: 1.5rem;
  border-radius: var(--radius-2xl);
}

.cd__icon--danger { background: rgb(220 38 38 / 0.12); color: var(--danger-500); }
.cd__icon--brand  { background: var(--accent-subtle);   color: var(--accent); }

.cd__title { font-size: var(--text-xl); margin-bottom: var(--space-2); }

.cd__message {
  margin: 0 0 var(--space-3);
  font-size: var(--text-sm);
  color: var(--text-secondary);
  line-height: 1.6;
}

.cd__item {
  margin: 0 0 var(--space-4);
  padding: var(--space-3) var(--space-4);
  font-weight: 700;
  color: var(--text-primary);
  background: var(--bg-sunken);
  border-radius: var(--radius-md);
  word-break: break-word;
}

.cd__warning {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  margin: 0 0 var(--space-6);
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--danger-500);
}

.cd__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3);
}

.dialog-enter-active { transition: opacity 200ms var(--ease-out-quart); }
.dialog-leave-active { transition: opacity 160ms var(--ease-out-quart); }
.dialog-enter-from, .dialog-leave-to { opacity: 0; }

.dialog-enter-active .cd__panel { transition: transform 280ms var(--ease-spring); }
.dialog-enter-from .cd__panel { transform: scale(0.9) translateY(12px); }
.dialog-leave-active .cd__panel { transition: transform 160ms var(--ease-out-quart); }
.dialog-leave-to .cd__panel { transform: scale(0.96); }
</style>
